<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="fa-solid" :class="editMode ? 'fa-edit' : 'fa-plus'"></i>
          {{ editMode ? 'Edit Timeslot' : this.assignNewTimeSlots ? 'Assign New Time Slots' : 'Create New Timeslot' }}
        </h4>
        <button class="modal-close" @click="closeModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="form-container">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label-theme">Appointment Types <span class="required">*</span></label>
              <div class="multiselect-wrapper" :aria-disabled="this.assignNewTimeSlots">
                <div class="selected-items" @click="toggleTypeDropdown"
                  :class="{ 'is-invalid': validationErrors.appointmentTypeIds }">
                  <span v-if="selectedTypes.length === 0" class="placeholder">
                    Select appointment types...
                  </span>
                  <span v-else class="selected-tags">
                    <span v-for="type in selectedTypes" :key="type.id" class="item-tag">
                      {{ type.description || type.label }}
                      <span class="remove-tag" @click.stop="removeType(type.id)">×</span>
                    </span>
                  </span>
                  <i class="fa-solid fa-chevron-down dropdown-icon"></i>
                </div>

                <div v-if="showTypeDropdown" class="items-dropdown">
                  <div class="items-search">
                    <input v-model="typeSearch" type="text" placeholder="Search appointment types..."
                      class="search-input" />
                  </div>
                  <div v-if="isAppointmentLoading" class="p-3 text-center text-muted">
                    Loading appointment types...
                  </div>

                  <div v-else class="items-options">
                    <div v-for="type in this.appointmentTypes" :key="type.id" class="item-option"
                      :class="{ selected: isTypeSelected(type.id) }" @click="toggleTypeSelection(type)">
                      <div class="option-content">
                        <span class="option-label">{{ type.description || type.label }}</span>
                      </div>
                      <i v-if="isTypeSelected(type.id)" class="fa-solid fa-check check-icon"></i>
                    </div>
                  </div>
                  <div class="items-dropdown-footer">
                    <button type="button" class="btn-close-dropdown" @click="closeTypeDropdown">
                      Done
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="validationErrors.appointmentTypeIds" class="validation-error">
                {{ validationErrors.appointmentTypeIds }}
              </div>
            </div>
            <div class="col-md-12">
              <label class="form-label-theme">Day <span class="required">*</span></label>
              <select v-model="timeslotData.day" class="form-select-theme"
                :class="{ 'is-invalid': validationErrors.day }" :disabled="autoSelectedDay">
                <option value="">Select Day</option>
                <option v-for="day in daysOfWeek" :key="day" :value="day">{{ day }}</option>
              </select>
              <div v-if="validationErrors.day" class="validation-error">
                {{ validationErrors.day }}
              </div>
            </div>

            <!-- Available Timeslots Section -->
            <div v-if="timeslotData.day && !editMode && this.assignNewTimeSlots" class="col-12">
              <div class="available-timeslots-section">
                <label class="form-label-theme">Available Timeslots for {{ timeslotData.day }}</label>
                <div v-if="isFetchingAvailableSlots" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                  <small class="text-muted d-block mt-2">Loading available timeslots...</small>
                </div>
                <div v-else-if="availableTimeslots && availableTimeslots.length" class="timeslots-grid">
                  <div v-for="slot in availableTimeslots" :key="slot.id" class="timeslot-card"
                    :class="{ selected: isTimeslotSelected(slot.id) }" @click="selectTimeslot(slot)">
                    <div class="timeslot-time">
                      {{ formatTimeDisplay(slot.startTime) }} - {{ formatTimeDisplay(slot.endTime) }}
                    </div>
                    <i v-if="isTimeslotSelected(slot.id)" class="fa-solid fa-check-circle timeslot-check"></i>
                  </div>
                </div>
                <div v-else class="empty-timeslots">
                  <i class="fa-regular fa-calendar-xmark"></i>
                  <p>No available timeslots for {{ timeslotData.day }}</p>
                </div>
              </div>
            </div>





            <div class="col-md-6" v-if="!this.assignNewTimeSlots">
              <label class="form-label-theme">Start Time <span class="required">*</span></label>

              <input v-model="timeslotData.startTime" type="time" class="form-control-theme"
                :class="{ 'is-invalid': validationErrors.startTime }" @input="validateTimeRange('start')"
                @blur="validateTimeRange('both')" step="300" />

              <div v-if="validationErrors.startTime" class="validation-error">
                {{ validationErrors.startTime }}
              </div>
            </div>

            <div class="col-md-6" v-if="!this.assignNewTimeSlots">
              <label class="form-label-theme">End Time <span class="required">*</span></label>
              <input v-model="timeslotData.endTime" type="time" class="form-control-theme"
                :class="{ 'is-invalid': validationErrors.endTime }" @input="validateTimeRange('end')"
                @blur="validateTimeRange('both')" step="300" />

              <div v-if="validationErrors.endTime" class="validation-error">
                {{ validationErrors.endTime }}
              </div>
            </div>

            <div class="col-md-12" v-if="!this.assignNewTimeSlots">
              <label class="form-label-theme">Duration (Minutes)</label>
              <input v-model.number="timeslotData.duration" type="number" class="form-control-theme"
                placeholder="e.g. 30" />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-theme-secondary" @click="closeModal">
          Cancel
        </button>
        <button v-if="editMode" class="btn-theme-outline-danger me-2" @click="handleDelete">
          <i class="fa-solid fa-trash me-2"></i>Delete
        </button>

        <template v-if="editMode && !this.assignNewTimeSlots">
          <button class="btn-theme-success" @click="handleSave(true)" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            Update Timeslot
          </button>
        </template>

        <template v-else-if="this.assignNewTimeSlots">
          <button class="btn-theme-success" @click="handleSave(true)"
            :disabled="isSaving || selectedTimeslotIds.length === 0">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            Add {{ selectedTimeslotIds.length }} Timeslot(s)
          </button>
        </template>

        <template v-else>
          <button class="btn-theme-success" @click="handleSave(true)" :disabled="isSaving || !!timeslotData.duration">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            Generate Single Slot
          </button>
          <button class="btn-theme-success" @click="handleSave(false)" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            Generate Multiple Slots
          </button>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import { TimeslotService } from "@/services/timeslot.service";

export default {
  name: "TimeslotModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    timeslot: {
      type: Object,
      default: null
    },
    appointmentTypes: {
      type: Array,
      default: () => []
    },
    isAppointmentLoading: {
      type: Boolean,
      default: false
    },
    selectedDay: {
      type: String,
      default: ""
    },
    selectedAppointmentTypeId: {
      type: String,
      default: ""
    },
    doctorId: {
      type: String,
      default: ""
    },
    assignNewTimeSlots: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'saved', 'deleted', 'fetch-timeslots', 'timeslots-updated'],
  data() {
    return {
      isSaving: false,
      showTypeDropdown: false,
      typeSearch: "",
      selectedTypes: [],
      validationErrors: {
        day: "",
        startTime: "",
        endTime: "",
        appointmentTypeIds: ""
      },


      daysOfWeek: [
        "Monday", "Tuesday", "Wednesday", "Thursday",
        "Friday", "Saturday", "Sunday"
      ],

      timeslotData: {
        day: "",
        startTime: "",
        endTime: "",
        appointmentTypeIds: [],
        duration: "",
      },

      // Available timeslots
      availableTimeslots: [],
      isFetchingAvailableSlots: false,
      selectedTimeslotIds: [],
      autoSelectedDay: false,
      autoSelectedType: false,
    };
  },
  computed: {
    editMode() {
      return this.timeslot && this.timeslot.id;
    },

    filteredAppointmentTypes() {
      if (!this.typeSearch) {
        return this.appointmentTypes.filter(type => type.isActive);
      }
      const search = this.typeSearch.toLowerCase();
      return this.appointmentTypes.filter(type =>
        type.isActive && (
          type.label.toLowerCase().includes(search) ||
          type.code.toLowerCase().includes(search)
        )
      );
    }
  },
  watch: {
    async show(newVal) {
      if (newVal) {
        this.initializeForm();
      }
    },

    timeslot: {
      handler(newVal) {
        if (newVal) {
          this.initializeForm();
        }
      },
      deep: true
    },

    'timeslotData.day': {
      handler(newVal) {
        if (newVal && !this.editMode && this.assignNewTimeSlots) {
          this.fetchAvailableTimeslots(newVal);
        }
      }
    }
  },
  methods: {
    initializeForm() {
      if (this.timeslot && this.timeslot.id) {
        // Edit mode
        this.timeslotData = {
          day: this.timeslot.day,
          startTime: this.timeslot.startTime,
          endTime: this.timeslot.endTime,
          appointmentTypeIds: this.timeslot.appointmentTypeIds || [],
          duration: this.timeslot.duration || "",
        };

        // Set selected types
        this.selectedTypes = this.appointmentTypes.filter(type =>
          this.timeslot.appointmentTypeIds.includes(type.id)
        );

      } else {
        // Create mode - auto-select day and appointment type if provided
        this.resetForm();

        // Auto-select day from props
        if (this.selectedDay) {
          this.timeslotData.day = this.selectedDay;
          this.autoSelectedDay = true;
        }

        // Auto-select appointment type from props
        if (this.selectedAppointmentTypeId && this.appointmentTypes.length) {
          const typeObj = this.appointmentTypes.find(t => t.id === this.selectedAppointmentTypeId);
          if (typeObj) {
            this.selectedTypes = [typeObj];
            this.timeslotData.appointmentTypeIds = [this.selectedAppointmentTypeId];
            this.autoSelectedType = true;
          }
        }

        // Fetch available timeslots if day is set
        if (this.timeslotData.day) {
          this.fetchAvailableTimeslots(this.timeslotData.day);
        }
      }

      this.validationErrors = {
        day: "",
        startTime: "",
        endTime: "",
        appointmentTypeIds: ""
      };
    },

    resetForm() {
      this.timeslotData = {
        day: "",
        startTime: "",
        endTime: "",
        appointmentTypeIds: [],
        duration: "",
      };
      this.selectedTypes = [];
      this.typeSearch = "";
      this.showTypeDropdown = false;
    },

    isTypeSelected(typeId) {
      return this.selectedTypes.some(type => type.id === typeId);
    },

    toggleTypeSelection(type) {
      if (this.assignNewTimeSlots) return;

      const index = this.selectedTypes.findIndex(t => t.id === type.id);
      if (index === -1) {
        this.selectedTypes.push(type);
      } else {
        this.selectedTypes.splice(index, 1);
      }
    },

    removeType(typeId) {
      this.selectedTypes = this.selectedTypes.filter(type => type.id !== typeId);
    },

    toggleTypeDropdown() {
      this.showTypeDropdown = !this.showTypeDropdown;
      this.typeSearch = "";
    },

    closeTypeDropdown() {
      this.showTypeDropdown = false;
      this.timeslotData.appointmentTypeIds = this.selectedTypes.map(type => type.id);
      this.validateField('appointmentTypeIds');
    },

    /**
     * Fetch available timeslots for a specific day
     */
    async fetchAvailableTimeslots(day) {
      if (!day) return;

      this.isFetchingAvailableSlots = true;
      this.availableTimeslots = [];
      this.selectedTimeslotIds = [];

      try {
        const { TimeslotService } = await import('@/services/timeslot.service');
        const response = await TimeslotService.getTimeslotsByDay(day);

        if (response && response.data) {
          // Handle nested response structure
          let data = response.data.data || response.data;

          // Ensure data is an array
          if (Array.isArray(data)) {
            this.availableTimeslots = data;
            console.log(`✅ Fetched ${data.length} timeslots for ${day}:`, data);
          } else if (data && typeof data === 'object') {
            // If it's an object, check for data property
            const slots = data.data || data.timeslots || [];
            this.availableTimeslots = Array.isArray(slots) ? slots : [];
            console.log(`✅ Extracted timeslots for ${day}:`, this.availableTimeslots);
          } else {
            this.availableTimeslots = [];
            console.warn('⚠️ Unexpected API response format:', response);
          }
        }
      } catch (error) {
        console.error("❌ Error fetching available timeslots:", error);
        this.availableTimeslots = [];
      } finally {
        this.isFetchingAvailableSlots = false;
      }
    },

    /**
     * Check if a timeslot is selected
     */
    isTimeslotSelected(slotId) {
      return this.selectedTimeslotIds.includes(slotId);
    },

    /**
     * Select/deselect a timeslot
     */
    selectTimeslot(slot) {
      const slotId = slot.id || slot._id;
      const index = this.selectedTimeslotIds.indexOf(slotId);

      if (index === -1) {
        // Select the slot
        this.selectedTimeslotIds.push(slotId);
      } else {
        // Deselect the slot
        this.selectedTimeslotIds.splice(index, 1);
      }
    },

    /**
     * Update doctor slots with selected timeslots
     */
    async updateDoctorSlotsWithSelection() {
      if (!this.selectedTimeslotIds.length) {
        this.$store.commit("toast/NEW", {
          type: "warning",
          message: "Please select at least one timeslot"
        });
        return;
      }

      try {
        const { DoctorTimeSlotService } = await import('@/services/doctor-time-slots.service');

        const payload = {
          slots: this.selectedTimeslotIds,
          doctorId: this.doctorId || this.$route.params.doctorId,
          appointmentTypeIds: this.timeslotData.appointmentTypeIds
        };

        const response = await DoctorTimeSlotService.updateDoctorSlots(payload);

        if (response.data?.data) {
          this.$store.commit("toast/NEW", {
            type: "success",
            message: "Timeslots added successfully!"
          });

          this.$emit('timeslots-updated', {
            slots: this.selectedTimeslotIds,
            appointmentTypeIds: this.timeslotData.appointmentTypeIds
          });

          this.$emit('fetch-timeslots');
          this.closeModal();
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: response?.message || "Failed to update doctor slots"
          });
        }
      } catch (error) {
        console.error("Error updating doctor slots:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "An error occurred while updating doctor slots"
        });
      }
    },


    validateTimeRange(type) {
      // Clear previous time errors
      this.validationErrors.startTime = "";
      this.validationErrors.endTime = "";

      // Validate start time
      if (type === 'start' || type === 'both') {
        // Validation removed as per user request
      }

      // Validate end time
      if (type === 'end' || type === 'both') {
        // Validation removed as per user request
      }
    },

    validateTimeRangeComplete() {
      const start = this.timeToMinutes(this.timeslotData.startTime);
      const end = this.timeToMinutes(this.timeslotData.endTime);

      // Check if end time is after start time
      if (end <= start) {
        return {
          isValid: false,
          error: "End time must be after start time",
          errorType: 'end'
        };
      }

      // Check if duration is reasonable (at least 15 minutes)
      if ((end - start) < 15) {
        return {
          isValid: false,
          error: "Minimum duration is 15 minutes",
          errorType: 'end'
        };
      }

      return { isValid: true };
    },
    // validateTimeRangeComplete() {
    //   const start = this.timeToMinutes(this.timeslotData.startTime);
    //   const end = this.timeToMinutes(this.timeslotData.endTime);

    //   // Check if end time is after start time
    //   if (end <= start) {
    //     return { 
    //       isValid: false, 
    //       error: "End time must be after start time",
    //       errorType: 'end'
    //     };
    //   }

    //   // Check if duration is reasonable (at least 15 minutes)
    //   if ((end - start) < 15) {
    //     return {
    //       isValid: false,
    //       error: "Minimum duration is 15 minutes",
    //       errorType: 'end'
    //     };
    //   }

    //   return { isValid: true };
    // },

    validateForm() {
      this.validationErrors = { day: "", startTime: "", endTime: "", appointmentTypeIds: "" };
      let isValid = true;

      // ✅ Day is always required
      if (!this.timeslotData.day && this.assignNewTimeSlots) {
        this.validationErrors.day = "Day is required";
        isValid = false;
      }

      // ✅ Appointment type always required
      if (!this.timeslotData.appointmentTypeIds.length && this.assignNewTimeSlots) {
        this.validationErrors.appointmentTypeIds =
          "At least one appointment type is required";
        isValid = false;
      }

      // 🚫 SKIP time validation when assigning existing slots
      if (this.assignNewTimeSlots) {
        return isValid;
      }

      if (!this.timeslotData.startTime) {
        this.validationErrors.startTime = "Start time is required";
        isValid = false;
      }

      // Validate end time
      if (!this.timeslotData.endTime) {
        this.validationErrors.endTime = "End time is required";
        isValid = false;
      }

      // Validate time range if both times are present
      if (this.timeslotData.startTime && this.timeslotData.endTime) {
        const rangeValidation = this.validateTimeRangeComplete();
        if (!rangeValidation.isValid) {
          if (rangeValidation.errorType === 'start') {
            this.validationErrors.startTime = rangeValidation.error;
          } else {
            this.validationErrors.endTime = rangeValidation.error;
          }
          isValid = false;
        }
      }

      if (!this.timeslotData.day) {
        this.validationErrors.day = "Day is required";
        isValid = false;
      }

      if (!this.timeslotData.startTime) {
        this.validationErrors.startTime = "Start time is required";
        isValid = false;
      }

      if (!this.timeslotData.endTime) {
        this.validationErrors.endTime = "End time is required";
        isValid = false;
      } else if (this.timeslotData.startTime && this.timeslotData.endTime) {
        const range = this.normalizeTimeRange(
          this.timeslotData.startTime,
          this.timeslotData.endTime
        );

        if (!range) {
          this.validationErrors.endTime = "Invalid time format";
          isValid = false;
        } else {
          const { start, end } = range;
          const duration = end - start;

          if (duration <= 0) {
            this.validationErrors.endTime = "End time must be after start time";
            isValid = false;
          }
        }
      }



      if (this.timeslotData.appointmentTypeIds.length === 0) {
        this.validationErrors.appointmentTypeIds = "At least one appointment type is required";
        isValid = false;
      }

      return isValid;
    },

    validateField(field) {
      this.validationErrors[field] = "";

      switch (field) {
        case 'appointmentTypeIds':
          if (this.timeslotData.appointmentTypeIds.length === 0) {
            this.validationErrors.appointmentTypeIds = "At least one appointment type is required";
          }
          break;
      }
    },

    /**
     * Convert UTC time to local time
     * @param {string} utcTime - Time in format HH:mm (UTC)
     * @returns {string} - Local time in format HH:mm
     */
    // convertUtcToLocal(utcTime) {
    //   if (!utcTime) return "";
    //   const date = new Date();
    //   const [hours, minutes] = utcTime.split(':').map(Number);

    //   // Create a date object with the UTC time
    //   const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes));

    //   const localHours = utcDate.getHours();
    //   const localMinutes = utcDate.getMinutes();

    //   return `${String(localHours).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}`;
    // },
    
    convertToLocalTime(date, time) {
      if (!time) return null;

      let hours, minutes;

      // ISO format
      if (time.includes("T")) {
        const d = new Date(time);
        if (isNaN(d)) return null;
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      } else {
        const parts = time.split(":");
        if (parts.length < 2) return null;
        [hours, minutes] = parts.map(Number);
      }

      // subtract 6 hours
      hours = hours - 6;

      if (hours < 0) {
        hours += 24;
      }

      const localDate = new Date();
      localDate.setHours(hours);
      localDate.setMinutes(minutes);
      localDate.setSeconds(0);

      return localDate;
    },

    formatTimeDisplay(time) {
      if (!time) return "";

      const localDate = this.convertToLocalTime(null, time);
      if (!localDate) return "";

      return localDate.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    // formatTimeDisplay(time) {
    //   if (!time) return "";

    //   try {
    //     // Convert UTC to local time first
    //     const localTime = this.convertUtcToLocal(time);

    //     const [hours, minutes] = localTime.split(":").map(Number);
    //     const period = hours >= 12 ? "PM" : "AM";
    //     const displayHours = hours % 12 || 12;
    //     const formatted = `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;

    //     // Log conversion for debugging
    //     console.log(`⏰ Converted UTC ${time} → Local ${localTime} → Display ${formatted}`);

    //     return formatted;
    //   } catch (error) {
    //     console.error(`❌ Error formatting time ${time}:`, error);
    //     return time;
    //   }
    // },

    async handleSave(isSingleSlot) {
      if (!this.validateForm()) return;

      // ✅ ASSIGN MODE → ONLY assign selected slots
      if (this.assignNewTimeSlots && this.selectedTimeslotIds.length > 0) {
        return this.updateDoctorSlotsWithSelection();
      }

      this.isSaving = true;
      try {
        let response;

        if (this.editMode) {
          // Prepare payload for Update (PATCH)
          // Prepare payload for Update (PATCH)
          const payload = {
            startTime: this.convertLocalTimeToUTC(this.timeslotData.startTime),
            endTime: this.convertLocalTimeToUTC(this.timeslotData.endTime),
            appointmentTypeIds: this.timeslotData.appointmentTypeIds,
          };

          // Use TimeslotService for update
          response = await TimeslotService.update(this.timeslot.id, payload);

          if (response?.success || response?.status === 200 || (response?.data && response?.data?.status === 200)) {
            this.$emit('fetch-timeslots');
            this.$emit('saved', response.data ? response.data : response);
            this.$store.commit("toast/NEW", {
              type: "success",
              message: response?.data?.data?.message || "Timeslot updated successfully!"
            });
            this.closeModal();
          } else {
            this.$store.commit("toast/NEW", {
              type: "error",
              message: response?.data?.data?.message || "Failed to update timeslot"
            });
          }

        } else {
          // Create Logic (keep using TimeslotService for now if that is the intended flow for creating)
          const payload = {
            day: this.timeslotData.day,
            startTime: this.convertLocalTimeToUTC(this.timeslotData.startTime),
            endTime: this.convertLocalTimeToUTC(this.timeslotData.endTime),
            appointmentTypeIds: this.timeslotData.appointmentTypeIds,
            ...(!isSingleSlot && this.timeslotData.duration && { duration: this.timeslotData.duration }),
            createType: isSingleSlot ? 'single' : 'range',
          };

          response = await TimeslotService.create(payload);

          if (response.data.status === 200) {
            this.$store.commit("toast/NEW", {
              type: "success",
              message: response?.data?.data?.message || "Timeslot created successfully!"
            });
            this.$emit('fetch-timeslots');
            this.$emit('saved', response.data.data.message);
            this.closeModal();
          } else {
            this.$store.commit("toast/NEW", {
              type: "error",
              message: response?.data?.data?.message || "Failed to save timeslot"
            });
          }
        }
      } catch (error) {
        console.error("Error saving timeslot:", error);
        const errorMsg = error?.response?.data
        this.$store.commit("toast/NEW", {
          type: "error",
          message: errorMsg?.data?.data?.data?.message || errorMsg?.data?.data?.message || "An error occurred while saving timeslot",
          duration: 12000
        });
      } finally {
        this.isSaving = false;
      }
    },

    async handleDelete() {
      if (!confirm('Are you sure you want to delete this timeslot? This action cannot be undone.')) {
        return;
      }

      try {
        // Use DoctorTimeSlotService for v2 delete
        const response = await TimeslotService.delete(this.timeslot.id);

        if (response?.success || response?.data?.status === 200) {

          this.$emit('deleted', this.timeslot.id);
          this.$emit('fetch-timeslots'); // Refresh list
          this.$store.commit("toast/NEW", {
            type: "success",
            message: response?.data?.data?.message || "Timeslot deleted successfully!"
          });
          this.closeModal();
        } else {
          this.$store.commit("toast/NEW", {
            type: "error",
            message: response?.data?.data?.message || "Failed to delete timeslot"
          });
        }
      } catch (error) {
        console.error("Error deleting timeslot:", error);
        const errorMsg = error?.response?.data
        this.$store.commit("toast/NEW", {
          type: "error",
          message: errorMsg?.data?.data?.message || errorMsg?.data?.message || "An error occurred while deleting timeslot"
        });
      }
    },

    closeModal() {
      this.$emit('close');
    },

    normalizeTimeRange(startTime, endTime) {
      const start = this.timeToMinutes(startTime);
      const endRaw = this.timeToMinutes(endTime);

      // If either is missing, don't validate yet
      if (start === null || endRaw === null) {
        return null;
      }

      let end = endRaw;

      // Handle overnight slots
      if (end <= start) {
        end += 24 * 60;
      }

      return { start, end };
    },



    timeToMinutes(time) {
      console.log("time", time);
      if (!time || typeof time !== "string") return null;

      const parts = time?.split(":");
      if (parts.length !== 2) return null;

      const hours = Number(parts[0]);
      const minutes = Number(parts[1]);

      if (isNaN(hours) || isNaN(minutes)) return null;

      return hours * 60 + minutes;
    },

    // async fetchAppointmentTypes() {
    //   this.isAppointmentLoading = true;
    //   try {
    //     const response = await AppointmentTypeService.getAll();
    //     const data = response.data;

    //     console.log("Appointment types:", data);

    //     if (data.status === 200) {
    //       this.appointmentTypes = data.data.data || [];
    //       console.log("Appointment types:", JSON.parse(JSON.stringify(this.appointmentTypes)));
    //     } else {
    //       this.$store.commit("toast/NEW", {
    //         type: "error",
    //         message: data.message || "Failed to fetch appointment types"
    //       });
    //     }
    //   } catch (error) {
    //     console.error("Error fetching appointment types:", error);
    //     this.$store.commit("toast/NEW", {
    //       type: "error",
    //       message: "An error occurred while fetching appointment types"
    //     });
    //   } finally {
    //     this.isAppointmentLoading = false;
    //   }
    // },

    convertLocalTimeToUTC(time) {
      if (!time) return "";

      let [hours, minutes] = time.split(":").map(Number);

      // ADD exactly 6 hours (hardcoded)
      hours = hours + 6;

      // Handle wrap around to next day
      if (hours >= 24) {
        hours = hours - 24;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  height: 70%;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eef2f6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8faff;
  border-radius: 16px 16px 0 0;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0144A6;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #eef2f6;
  color: #dc3545;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.form-container {
  padding: 10px;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #eef2f6;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8faff;
  border-radius: 0 0 16px 16px;
}

/* Form Styles */
.form-label-theme {
  font-weight: 600;
  margin-bottom: 6px;
  font-size: 14px;
  color: #444;
  display: block;
}

.form-control-theme,
.form-select-theme {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s;
}

.form-control-theme:focus,
.form-select-theme:focus {
  border-color: #3c78f9;
  outline: none;
  box-shadow: 0 0 0 3px rgba(60, 120, 249, 0.1);
}

.form-control-theme.is-invalid,
.form-select-theme.is-invalid {
  border-color: #dc3545;
}

.required {
  color: #dc3545;
  font-weight: bold;
}

.validation-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  min-height: 18px;
}

/* Multiselect Styles */
.multiselect-wrapper {
  position: relative;
}

.selected-items {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px 15px;
  min-height: 50px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
}

.selected-items:hover {
  border-color: #3c78f9;
}

.selected-items.is-invalid {
  border-color: #dc3545;
}

.selected-items .placeholder {
  /* color: #999; */
  background-color: transparent !important;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.item-tag {
  background: #e9f2ff;
  color: #3c78f9;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.remove-tag {
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #999;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.remove-tag:hover {
  background: #dc3545;
  color: white;
}

.dropdown-icon {
  color: #666;
  transition: transform 0.2s;
}

.items-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  max-height: 300px;
  overflow-y: auto;
}

.items-search {
  padding: 10px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
}

.items-options::-webkit-scrollbar {
  width: 6px;
}

.items-options::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 6px;
}

.items-options::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.items-options {
  max-height: 180px;
  /* bigger list */
  overflow-y: auto;
  scroll-behavior: smooth;
}

.item-option {
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-option:hover {
  background: #f8faff;
}

.item-option.selected {
  background: #e9f2ff;
}

.option-content {
  display: flex;
  flex-direction: column;
}

.option-label {
  font-weight: 600;
  color: #333;
  font-size: small;
}

.option-code {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.check-icon {
  color: #3c78f9;
}

.items-dropdown-footer {
  padding: 10px;
  border-top: 1px solid #eee;
  text-align: right;
}

.btn-close-dropdown {
  background: #3c78f9;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

/* Button Styles */
.btn-theme-secondary {
  background: #f1f3f5;
  color: #333;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme-secondary:hover {
  background: #e9ecef;
}

.btn-theme-success {
  background: #0144A6;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme-success:hover:not(:disabled) {
  background: #0148b3de;
}

.btn-theme-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-theme-outline-danger {
  background: transparent;
  border: 2px solid #dc3545;
  color: #dc3545;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme-outline-danger:hover {
  background: #dc3545;
  color: white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    max-height: 95vh;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}

/* Business Hours Info */
.business-hours-info {
  background: #e9f7ef;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #155724;
}

.business-hours-info i {
  color: #28a745;
}

/* Time Display */
.time-display {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  font-weight: 500;
}

/* Available Timeslots Section */
.available-timeslots-section {
  background: #f8faff;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.timeslots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.timeslot-card {
  background: white;
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  font-size: 13px;
  font-weight: 500;
}

.timeslot-card:hover {
  border-color: #3c78f9;
  box-shadow: 0 2px 8px rgba(60, 120, 249, 0.1);
}

.timeslot-card.selected {
  background: #e9f2ff;
  border-color: #3c78f9;
  box-shadow: 0 2px 8px rgba(60, 120, 249, 0.2);
}

.timeslot-time {
  color: #333;
  font-weight: 600;
}

.timeslot-check {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #3c78f9;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.empty-timeslots {
  text-align: center;
  padding: 30px 15px;
  color: #999;
}

.empty-timeslots i {
  font-size: 32px;
  color: #ddd;
  margin-bottom: 10px;
  display: block;
}

.empty-timeslots p {
  margin: 10px 0 0 0;
  font-size: 14px;
}
</style>